import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ShieldCheck, Upload, Calendar, User, 
  CheckCircle, XCircle, RefreshCcw, Info, AlertTriangle, Building, Copy 
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import API from '../services/api';

const MaLicence = () => {
  const { t } = useTranslation();

  // --- 1. ÉTATS ---
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [modal, setModal] = useState({ show: false, title: '', message: '', type: 'info' });
  const [copiedId, setCopiedId] = useState(false);

  // --- 2. RÉCUPÉRATION DES INFOS DE LICENCE CLOUD ---
  const fetchLicenseStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get(`/license/status?t=${Date.now()}`);
      setLicense(res.data);
    } catch (err) {
      console.error("❌ Erreur récupération licence:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLicenseStatus();
  }, [fetchLicenseStatus]);

  // --- 3. LOGIQUE DE MISE À JOUR DE LA LICENCE ---
  const handleUpdateLicense = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.name !== 'metadata.dat') {
      setModal({ 
        show: true, 
        title: t('license.modal_invalid_title') || "Fichier invalide", 
        message: t('license.modal_invalid_message') || "Le fichier doit être nommé exactement 'metadata.dat'.", 
        type: 'alert' 
      });
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const content = event.target.result;
        const res = await API.post('/license/update', { 
          licenseData: content.trim() 
        });

        if (res.data.success) {
          if (res.data.newStatus) {
            setLicense(res.data.newStatus);
          }

          setModal({ 
            show: true, 
            title: t('license.modal_success_title') || "Succès", 
            message: t('license.modal_success_message') || "Licence conforme et mise à jour avec succès !", 
            type: 'confirm' 
          });

          setTimeout(() => {
            window.location.reload(); 
          }, 2000);
        }
      } catch (err) {
        const errorMsg = err.response?.data?.error || "Le fichier est corrompu ou non associé à cette entreprise.";
        setModal({ 
          show: true, 
          title: t('license.modal_error_title') || "Échec de la mise à jour", 
          message: errorMsg, 
          type: 'alert' 
        });
      } finally {
        setIsUploading(false);
        e.target.value = '';
      }
    };
    
    reader.readAsText(file);
  };

  return (
    <div style={s.container}>
      <Sidebar />
      <main style={s.main}>
        
        {/* MODAL DE NOTIFICATION */}
        {modal.show && (
          <div style={s.modalOverlay}>
            <div style={s.modalCard}>
              <div style={s.modalHeader}>
                {modal.type === 'alert' ? <AlertTriangle color="#ef4444" /> : <ShieldCheck color="#2563eb" />}
                <h3 style={s.modalTitle}>{modal.title}</h3>
              </div>
              <p style={s.modalMessage}>{modal.message}</p>
              <div style={s.modalActions}>
                <button onClick={() => setModal({ ...modal, show: false })} style={s.btnModalConfirm}>OK</button>
              </div>
            </div>
          </div>
        )}

        <header style={s.header}>
          <div>
            <h1 style={s.headerTitle}>{t('license.page_title') || "Gestion de la Licence"}</h1>
            <p style={s.headerSubtitle}>{t('license.page_subtitle') || "Informations sur l'abonnement Cloud de votre entreprise"}</p>
          </div>
          <button onClick={fetchLicenseStatus} style={s.btnRefresh} disabled={loading}>
            <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </header>

        <div style={s.contentArea}>
          
          {/* BANDEAU IDENTIFIANT ENTREPRISE (CID) */}
          <div style={{ 
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', 
            padding: '20px', marginBottom: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', textAlign: 'left' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Building size={16} color="#4f46e5" />
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                IDENTIFIANT DE L'ENTREPRISE (CID POUR LA LICENCE)
              </label>
            </div>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 14px 0', lineHeight: '1.4' }}>
              Communiquez cet identifiant à l'administrateur pour générer le certificat de licence associé à votre structure.
            </p>
            
            <div style={{ 
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
              background: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 16px', borderRadius: '10px', position: 'relative'
            }}>
              <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#1e293b', fontSize: '14px', wordBreak: 'break-all' }}>
                {license?.db_local_id || license?.companyId || license?.id || "Chargement..."}
              </span>
              
              <button 
                type="button"
                onClick={() => {
                  const companyIdToCopy = license?.db_local_id || license?.companyId || license?.id;
                  if (companyIdToCopy) {
                    navigator.clipboard.writeText(companyIdToCopy);
                    setCopiedId(true);
                    setTimeout(() => setCopiedId(false), 2000);
                  }
                }}
                style={{ 
                  background: '#fff', border: '1px solid #cbd5e1', padding: '6px 12px', 
                  borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                  fontSize: '12px', fontWeight: '600', color: '#334155', transition: 'all 0.15s ease'
                }}
              >
                <Copy size={14} /> Copier
              </button>
              {copiedId && <span style={{ position: 'absolute', top: '-25px', right: '12px', background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>Copié !</span>}
            </div>
          </div>

          <div style={s.licenseGrid}>
            
            {/* CARTE INFO CLIENT & ABONNEMENT */}
            <div style={s.infoCard}>
              <h3 style={s.cardTitle}><User size={18} /> Informations Abonnement</h3>
              <div style={s.dataRow}>
                <span style={s.label}>Détenteur :</span>
                <span style={s.value}>{!license?.nom ? 'Non enregistré' : license.nom}</span>
              </div>
              <div style={s.dataRow}>
                <span style={s.label}>Date d'expiration :</span>
                <span style={{...s.value, color: '#2563eb'}}>
                  <Calendar size={14} /> {license?.exp || 'Aucune licence active'}
                </span>
              </div>
              <div style={s.dataRow}>
                <span style={s.label}>Statut :</span>
                {license?.valid ? (
                  <span style={s.statusActive}><CheckCircle size={12}/> ACTIF</span>
                ) : (
                  <span style={s.statusInactive}><XCircle size={12}/> EXPIRÉ OU INVALIDE</span>
                )}
              </div>
            </div>

            {/* ZONE D'UPLOAD DU CERTIFICAT CLOUD */}
            <div style={s.uploadCard}>
              <h3 style={{...s.cardTitle, color: '#1e40af'}}><Upload size={18} /> Mettre à jour</h3>
              <p style={s.uploadText}>
                Pour prolonger votre abonnement ou activer de nouveaux modules, téléverser le fichier metadata.dat généré pour votre entreprise.
              </p>
              <label style={isUploading ? s.uploadZoneDisabled : s.uploadZone}>
                <Upload size={32} color="#3b82f6" />
                <span style={s.uploadLabel}>{isUploading ? 'Traitement cryptographique...' : 'Importer le certificat'}</span>
                <input 
                    type="file" 
                    style={{ display: 'none' }} 
                    accept=".dat" 
                    onChange={handleUpdateLicense} 
                    disabled={isUploading} 
                />
              </label>
            </div>

          </div>

          {/* TABLEAU UNIQUE DES MODULES ACTIVÉS */}
          <div style={s.moduleSection}>
            <h3 style={s.sectionTitle}><Info size={18} /> Modules activés sur cette entreprise</h3>
            <div style={s.moduleGrid}>
              {license?.allowed_modules && license.allowed_modules.length > 0 ? (
                license.allowed_modules
                  .filter(mod => mod.toUpperCase() !== 'SYSTEM' && mod.toUpperCase() !== 'FULL_ACCESS')
                  .map((mod) => (
                    <div key={mod} style={s.moduleBadge}>
                      <CheckCircle size={12} color="#10b981" /> 
                      {mod.replace('_', ' ').toUpperCase()}
                    </div>
                  ))
              ) : (
                <p style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>
                    Aucun module spécifique activé (Accès restreint à la configuration de base).
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// --- STYLES EN OBJETS ---
const s = {
  container: { display: 'flex', height: '100vh', width: '100vw', background: '#f8fafc', overflow: 'hidden' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflowY: 'auto' },
  header: { background: 'white', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' },
  headerTitle: { margin: 0, fontSize: '22px', fontWeight: '900', color: '#0f172a' },
  headerSubtitle: { margin: 0, color: '#64748b', fontSize: '13px' },
  btnRefresh: { background: 'none', border: '1px solid #e2e8f0', padding: '8px', borderRadius: '8px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  contentArea: { padding: '25px 40px' },
  licenseGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px', marginBottom: '25px' },
  infoCard: { background: 'white', padding: '25px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', textAlign: 'left' },
  uploadCard: { background: '#eff6ff', padding: '25px', borderRadius: '12px', border: '1px solid #bfdbfe', textAlign: 'left' },
  cardTitle: { marginTop: 0, marginBottom: '20px', fontSize: '16px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' },
  dataRow: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f1f5f9' },
  label: { fontSize: '13px', color: '#64748b', fontWeight: '600' },
  value: { fontSize: '14px', color: '#0f172a', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' },
  statusActive: { padding: '4px 10px', borderRadius: '20px', background: '#dcfce7', color: '#166534', fontSize: '11px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '5px' },
  statusInactive: { padding: '4px 10px', borderRadius: '20px', background: '#fee2e2', color: '#991b1b', fontSize: '11px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '5px' },
  uploadText: { fontSize: '13px', color: '#1e40af', marginBottom: '20px', lineHeight: '1.5' },
  uploadZone: { height: '120px', border: '2px dashed #3b82f6', borderRadius: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fff', transition: '0.2s' },
  uploadZoneDisabled: { height: '120px', border: '2px dashed #cbd5e1', borderRadius: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', opacity: 0.6 },
  uploadLabel: { marginTop: '10px', fontSize: '13px', fontWeight: '800', color: '#3b82f6' },
  moduleSection: { background: 'white', padding: '25px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', textAlign: 'left' },
  sectionTitle: { marginTop: 0, marginBottom: '15px', fontSize: '15px', fontWeight: '800', color: '#475569', display: 'flex', alignItems: 'center', gap: '10px' },
  moduleGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
  moduleBadge: { padding: '8px 15px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', color: '#334155' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(15, 23, 42, 0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 },
  modalCard: { background: 'white', padding: '25px', borderRadius: '12px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
  modalHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  modalTitle: { margin: 0, fontSize: '16px', fontWeight: '900' },
  modalMessage: { color: '#64748b', fontSize: '14px', marginBottom: '20px' },
  modalActions: { display: 'flex', justifyContent: 'flex-end' },
  btnModalConfirm: { padding: '8px 20px', background: '#2563eb', border: 'none', borderRadius: '6px', color: 'white', fontWeight: '800', cursor: 'pointer', transition: 'background 0.2s' }
};

export default MaLicence;